/**
 * Aiming contract types and constants — pure shared definitions.
 *
 * Defines the canonical discriminated intent, capability profile, launch input/result,
 * and wire-event payload interfaces for assisted projectile aiming (v1: mage_auto, fireball).
 *
 * Zero dependencies on DOM, Pixi, Colyseus runtime, ServiceRegistry, or Node server.
 */

/** IDs retain the existing string wire shape; admission validates capabilities. */
export type AssistedProjectileId = string;

/** Original tuned profiles, kept separate from dynamic projectile eligibility. */
export type TunedProjectileId =
  | 'mage_auto'
  | 'fireball'
  | 'druid_auto'
  | 'ranger_auto'
  | 'warlock_auto'
  | 'priest_auto'
  | 'assassin_auto';
export type Facing = 'left' | 'right';
export type Point = Readonly<{ x: number; y: number }>;
export type DirectionSource = 'pointer' | 'facing';

/** A fire-time alternative to the original locked target. Authority validates
 * that target first; this offer can only remove assistance, never add it. */
export type ProjectileFallbackOffer = Readonly<{
  source: DirectionSource;
  direction: Point;
  muzzleFacing: Facing;
  requested: boolean;
}>;

export type CastAimIntent =
  | Readonly<{ version: 1; mode: 'manual_locked' | 'assisted_auto'; targetId: string }>
  | Readonly<{ version: 1; mode: 'free_aim' | 'directional_fallback'; source: DirectionSource }>;

export type AimFailure = 'no_target' | 'out_of_range' | 'los_blocked' | 'invalid_target';

export type AimDecision =
  | Readonly<{ ok: true; intent: CastAimIntent }>
  | Readonly<{ ok: false; reason: AimFailure }>;

export type AssistedAimProfile = Readonly<{
  abilityId: AssistedProjectileId;
  range: number;
  windUpMs: number;
  speed: number;
  width: number;
  height: number;
  lifespanMs: number;
  homingStrength: number;
  maxTurnRate: number;
  homingDelayMs: number;
  /** Internal authoring override; never added to the wire intent or event shape. */
  forceStraight?: true;
  allowsDirectionalFallback: true;
  primaryImpactCount: 1;
}>;

export type LaunchCaster = Readonly<{
  position: Point;
  width?: number;
  height?: number;
  facing: Facing;
}>;

export type DirectionalLaunchInput =
  | Readonly<{ kind: 'point'; point?: Point | null }>
  | Readonly<{ kind: 'vector'; unit: Point; muzzleFacing: Facing }>;

export type AssistedLaunchInput = Readonly<{
  caster: LaunchCaster;
  profile: AssistedAimProfile;
  intent: CastAimIntent;
  targetPoint?: Point | null;
  directional?: DirectionalLaunchInput;
}>;

export type AssistedLaunchSuccess = Readonly<{
  ok: true;
  spawnX: number;
  spawnY: number;
  direction: Facing;
  unitX: number;
  unitY: number;
  targetId: string | null;
  guidance: 'authored' | 'straight';
}>;

export type AssistedLaunchFailureReason =
  | 'missing_target'
  | 'invalid_caster'
  | 'invalid_profile'
  | 'invalid_intent'
  | 'invalid_vector';

export type AssistedLaunchFailure = Readonly<{
  ok: false;
  reason: AssistedLaunchFailureReason;
}>;

export type AssistedLaunchResult = AssistedLaunchSuccess | AssistedLaunchFailure;

// ── Wire-event contracts (15-AIMING-DELIVERY-CONTRACT.md) ────────────────────

export type CastAbilityAimPayload = Readonly<{
  ability_id: AssistedProjectileId;
  client_cast_seq: number;
  aim: CastAimIntent;
  target_id?: string;
}>;

export type LaunchAbilityLegacyPayload = Readonly<{
  ability_id: 'death_coil';
  client_cast_seq: number;
}>;

export type LaunchAbilityAimPayload = Readonly<{
  version: 1;
  ability_id: AssistedProjectileId;
  client_cast_seq: number;
  direction?: Point;
  muzzleFacing?: Facing;
  fallback?: ProjectileFallbackOffer;
}>;

export type LaunchAbilityPayload = LaunchAbilityLegacyPayload | LaunchAbilityAimPayload;

export type ProjectileLaunchedEvent = Readonly<{
  version: 1;
  playerId: string;
  ability_id: AssistedProjectileId;
  client_cast_seq: number;
  effect_index: 0;
  aim: CastAimIntent;
  target_id: string | null;
  spawnX: number;
  spawnY: number;
  muzzleFacing: Facing;
  unitX: number;
  unitY: number;
  guidance: 'authored' | 'straight';
  speed: number;
  width: number;
  height: number;
  lifespanMs: number;
  homingStrength: number;
  maxTurnRate: number;
  homingDelayMs: number;
  launchedAt: number;
}>;

export type ProjectileRejectPhase = 'cast' | 'launch' | 'impact';

export type ProjectileRejectReason =
  | 'aim_protocol_required'
  | 'invalid_aim'
  | 'invalid_cast_sequence'
  | 'invalid_target'
  | 'no_target'
  | 'out_of_range'
  | 'los_blocked'
  | 'cast_not_accepted'
  | 'launch_expired'
  | 'already_launched'
  | 'invalid_projectile_impact'
  | 'life_changed'
  | 'capacity'
  /** A newer cast replaced this one before it launched; its mana and cooldown were refunded. */
  | 'replaced';

export type ProjectileRejectedEvent = Readonly<{
  version: 1;
  ability_id: string;
  client_cast_seq: number;
  phase: ProjectileRejectPhase;
  reason: ProjectileRejectReason;
}>;

export const ASSISTED_PROJECTILE_IDS: readonly TunedProjectileId[] = Object.freeze([
  'mage_auto',
  'fireball',
  'druid_auto',
  'ranger_auto',
  'warlock_auto',
  'priest_auto',
  'assassin_auto',
] as const);

export const DEFAULT_MUZZLE_OFFSET_X = 40;
export const DEFAULT_MUZZLE_OFFSET_Y = -5;
export const VECTOR_TOLERANCE = 0.001;

export function isAssistedProjectileId(id: string): id is TunedProjectileId {
  return (
    id === 'mage_auto' ||
    id === 'fireball' ||
    id === 'druid_auto' ||
    id === 'ranger_auto' ||
    id === 'warlock_auto' ||
    id === 'priest_auto' ||
    id === 'assassin_auto'
  );
}
